import { Container, Card, CardContent, CardHeader, Button, TextField, Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAPI } from "../../apiv1";
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { selectIsRoot } from "../../store/slices/session";
import { changeToNewOrganization, refreshOrganizations } from "../../store/thunk/auth";
import { API, Organization, OrganizationMemberRole } from "../../types/apiv1";

type RootAPI = ReturnType<API['organizations']['id']>['root'];

function OrganizationsCard() {
	const session = useAppSelector(state => state.session);
	if (!session.authenticated) throw new Error("unauthenticated");

	return <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
		<Card>
			<CardHeader title="Organizations JSON" />
			<CardContent>
				<pre>
					{JSON.stringify(session.organizations, null, "\t")}
				</pre>
			</CardContent>
		</Card>
	</Container>;
}

type CreateOrganizationCardProps = {
	rootAPI: RootAPI,
}

function CreateOrganizationCard({ rootAPI }: CreateOrganizationCardProps) {
	const dispatch = useAppDispatch();
	const [name, setName] = useState("");

	const submit = useCallback(() => {
		rootAPI.createOrganization(name).then(() => {
			alert("New organization created successfully.");
			dispatch(refreshOrganizations())
		}).catch(error => {
			alert(`Failed to create new organization: ${error}`)
		})
	}, [name, rootAPI]);
	
	return <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
		<Card>
			<CardHeader title="Create Organization" />
			<CardContent>
				<TextField
					label="Name"
					variant="outlined"
					value={name}
					onChange={e => setName(e.target.value)}
				/>
				<Button
					variant="outlined"
					onClick={submit}
				>
					Submit
				</Button>
			</CardContent>
		</Card>
	</Container>;
}

type AddUserCardProps = {
	organizations: Organization[],
	rootAPI: RootAPI,
}

function AddUserCard({ organizations, rootAPI }: AddUserCardProps) {
	const [organization, setOrganization] = useState("");
	const [email, setEmail] = useState("");
	const [role, setRole] = useState("");

	const submit = useCallback(() => {
		const numRole = parseInt(role, 10);

		rootAPI.addUserToOrganization(organization, email, numRole).then(() => {
			alert("Organisation member added or updated successfully.");
		}).catch(error => {
			alert(`Failed to add or update organisation member: ${error}`)
		})
	}, [organization, role, rootAPI]);
	
	return <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
		<Card>
			<CardHeader title="Add user to/Update user of organization" />
			<CardContent>
				<FormControl sx={{ minWidth: '200px' }}>
					<InputLabel id="root-page-add-user-org-select">
						Organisation
					</InputLabel>
					<Select
						id="root-page-add-user-org-select"
						label="Organisation"
						variant="outlined"
						value={organization}
						onChange={e => setOrganization(e.target.value)}
					>
						{organizations.map(org =>
							<MenuItem
								key={org.organization_id}
								value={org.organization_id}
							>
								{org.name}
							</MenuItem>
						)}
					</Select>
				</FormControl>
				<TextField
					label="Email"
					variant="outlined"
					type="email"
					value={email}
					onChange={e => setEmail(e.target.value)}
				/>
				<FormControl sx={{ minWidth: '200px' }}>
					<InputLabel id="root-page-add-user-role-select">
						Role
					</InputLabel>
					<Select
						id="root-page-add-user-role-select"
						label="Role"
						variant="outlined"
						value={role}
						onChange={e => setRole(e.target.value)}
					>
						<MenuItem value="20">Regular</MenuItem>
						<MenuItem value="80">Admin</MenuItem>
					</Select>
				</FormControl>
				<Button
					variant="outlined"
					onClick={submit}
				>
					Submit
				</Button>
			</CardContent>
		</Card>
	</Container>;
}

export default function RootPage() {
	const api = useAPI();
	const session = useAppSelector(state => state.session);
	if (!selectIsRoot(session)) return <Navigate to="/" />
	const rootAPI = api.organizations.id(session.activeOrganization).root;

	return <>
		<OrganizationsCard />
		<CreateOrganizationCard rootAPI={rootAPI} />
		<AddUserCard organizations={session.organizations} rootAPI={rootAPI} />
	</>;
}
